import React, { useCallback, useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import { SOURCE_ACTIVITIES_NAME } from "./constants";
import { ActivityProperties, MapEventListener, MarkersMap } from "./types";
import { createActivityElement } from "./helpers";
import { useActivity } from "../hooks";

interface UnclusteredMarkersProps {
   map: mapboxgl.Map;
}

const UnclusteredMarkers: React.FC<UnclusteredMarkersProps> = ({ map }) => {
   const markers = useRef<MarkersMap>({});
   const markersOnScreen = useRef<MarkersMap>({});
   const updateMarkersTimeout = useRef<NodeJS.Timeout | null>(null);
   const { userPictureCache } = useActivity();

   const updateMarkers = useCallback(() => {
      const newMarkers: MarkersMap = {};

      const features = map.querySourceFeatures(SOURCE_ACTIVITIES_NAME, {
         filter: ["!=", "cluster", true],
      }) as unknown as GeoJSON.Feature<GeoJSON.Point, ActivityProperties>[];

      for (const feature of features) {
         const { reservationId: markerId } = feature.properties;

         let marker = markers.current[markerId];

         if (!marker) {
            const el = document.createElement("div");
            const [longitude, latitude] = feature.geometry.coordinates;

            marker = markers.current[markerId] = new mapboxgl.Marker(el)
               .setLngLat([longitude, latitude])
               .addTo(map);

            createActivityElement(el, feature.properties, userPictureCache);
         }

         newMarkers[markerId] = marker;
         if (!markersOnScreen.current[markerId]) marker.addTo(map);
      }

      for (const id in markersOnScreen.current) {
         if (!newMarkers[id]) markersOnScreen.current[id].remove();
      }
      markersOnScreen.current = newMarkers;
   }, [map]);

   const debounceUpdateMarkers = useCallback(() => {
      if (updateMarkersTimeout.current) {
         clearTimeout(updateMarkersTimeout.current);
      }
      updateMarkersTimeout.current = setTimeout(() => {
         updateMarkers();
      }, 20);
   }, [updateMarkers]);

   useEffect(() => {
      const dataListener = (e: MapEventListener) => {
         if (e.sourceId !== SOURCE_ACTIVITIES_NAME || !e.isSourceLoaded) return;
         updateMarkers();
      };

      const renderListener = () => {
         if (!map.isSourceLoaded(SOURCE_ACTIVITIES_NAME)) return;
         debounceUpdateMarkers();
      };

      map.on("data", dataListener);
      map.on("render", renderListener);

      updateMarkers();

      return () => {
         map.off("data", dataListener);
         map.off("render", renderListener);
      };
   }, [map, updateMarkers, debounceUpdateMarkers]);

   return null;
};

export default UnclusteredMarkers;
